/**
 * Замечания к «Юниверситету» (16.10.2026): по месту долгого нажатия — к чему замечание. Разметку несут сами экраны:
 *   data-fb="item:<id>" + data-fb-name="задание 3/8"  — что это (якорь; из имён снаружи внутрь — путь «Комната › Квест › задание»);
 *   data-fb-part="вариант ответа"                       — какая часть (вопрос, картинка, пояснение…);
 *   data-fb-media="media/x.webp"                        — картинка/ролик пакета.
 * Цитата — текст самого нажатого места (вариант, абзац, кнопка), чтобы правка нашла его в базе знаний.
 * Чистая функция над узлом с getAttribute/parentElement — проверяется без браузера.
 */
import type { FbTarget } from "./fbApi";

export interface FbNode {
  tagName: string;
  textContent: string | null;
  /** HTML-элементы: текст как на экране (блоки — через перевод строки, скрытое — нет); у svg нет — берём textContent */
  innerText?: string;
  parentElement: FbNode | null;
  getAttribute(name: string): string | null;
}

/** Где цитата: ближайший «текстовый» элемент к пальцу (svg <text> — с маленькой буквы). */
const QUOTE_TAGS = new Set(["P", "LI", "BUTTON", "H1", "H2", "H3", "H4", "LABEL", "TEXT", "SMALL", "FIGCAPTION", "B", "STRONG"]);
const TAG_PART: Record<string, string> = { IMG: "картинка", VIDEO: "видео", AUDIO: "звук", BUTTON: "кнопка", INPUT: "поле ответа" };

const squash = (s: string | null | undefined) => (s ?? "").replace(/\s+/g, " ").trim();
const cut = (s: string, n: number) => { const a = [...s]; return a.length > n ? `${a.slice(0, n - 1).join("")}…` : s; };

export function locate(target: FbNode, rootName: string): FbTarget {
  let anchor = "", part = "", media: string | null = null;
  let quoteEl: FbNode | null = null, partEl: FbNode | null = null;
  const names: string[] = [];
  for (let e: FbNode | null = target; e; e = e.parentElement) {
    const tag = e.tagName.toUpperCase();
    if (!quoteEl && !anchor && QUOTE_TAGS.has(tag)) quoteEl = e;
    const p = e.getAttribute("data-fb-part");
    if (p && !part) { part = p; partEl = e; }
    const m = e.getAttribute("data-fb-media");
    if (m && !media) media = m;
    const a = e.getAttribute("data-fb");
    if (a) {
      if (!anchor) anchor = a;
      const nm = squash(e.getAttribute("data-fb-name"));
      if (nm && names[0] !== nm) names.unshift(nm);
    }
  }
  if (!part) part = TAG_PART[target.tagName.toUpperCase()] ?? "";
  /* цитата: часть с разметкой (вариант, вопрос) целиком, иначе ближайший текстовый элемент; у картинки — нет текста */
  const src = media ? null : partEl ?? quoteEl;
  const quote = cut(squash(src ? src.innerText ?? src.textContent : ""), 400);
  const place = cut([rootName, ...names].filter(Boolean).join(" › "), 300);
  return { anchor: anchor || "screen", part, place, quote, media };
}
